import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components';
import { motion } from 'framer-motion';
import { SparklesText } from '@/components/ui/sparkles-text';
// Removed unused import: import { useTheme } from 'next-themes';
import { useActions } from '@/contexts/ActionContext';
import { useAdmin } from '@/admin/contexts/AdminContext';
import { useMaintenanceMode } from '@/contexts/MaintenanceContext';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import AuthModal from '@/components/auth/AuthModal';
import EmailVerificationAlert from '@/components/auth/EmailVerificationAlert';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AuthButton } from '@/components/auth/AuthButton';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  // Removed unused theme variable
  const { isLoading } = useActions();
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const { isMaintenanceMode, maintenanceMessage } = useMaintenanceMode();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);

  // Show verification alert if user is logged in but not authenticated
  useEffect(() => {
    if (currentUser && !isAuthenticated) {
      setShowVerificationAlert(true);
    } else {
      setShowVerificationAlert(false);
    }
  }, [currentUser, isAuthenticated]);

  const enterForge = () => {
    // Only navigate to forge if user is authenticated and not in maintenance mode (or is admin)
    if (isAuthenticated && (!isMaintenanceMode || isAdmin)) {
      navigate('/forge');
    }
  };

  const openAuthModal = () => {
    setIsAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        {currentUser && isAdmin && !isAdminLoading && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
            className="relative"
            title="Admin Dashboard"
          >
            <ShieldCheck className="h-5 w-5 text-primary" />
          </Button>
        )}
        {currentUser && <AuthButton />}
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-2xl"
      >
        <div className="flex items-center justify-center mb-6">
          <img
            src="/images/elements.png"
            alt="Elements"
            className="w-20 h-20 object-contain drop-shadow-[2px_2px_3px_rgba(0,0,0,0.3)]"
          />
        </div>

        <div className="mb-6">
          <SparklesText
            text="VibeCraft"
            className="text-5xl md:text-6xl font-bold text-foreground"
            colors={{
              first: '#7C3AED', // Darker purple (violet-600)
              second: '#2563EB', // Darker blue (blue-600)
            }}
            sparklesCount={10}
          />
        </div>

        <p className="text-xl text-muted-foreground mb-8">
          Combine, deconstruct, and craft your way to discovery!
        </p>

        {/* Email Verification Alert */}
        {showVerificationAlert && <EmailVerificationAlert />}

        {/* Maintenance Mode Alert */}
        {isMaintenanceMode && currentUser && isAuthenticated && !isAdmin && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Forge Access Restricted</AlertTitle>
            <AlertDescription>{maintenanceMessage}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button
            size="lg"
            onClick={enterForge}
            disabled={
              !currentUser || isLoading || !isAuthenticated || (isMaintenanceMode && !isAdmin)
            }
            className="text-lg px-8 py-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            style={{
              transition: 'opacity 300ms ease, transform 300ms ease',
              backgroundImage: 'linear-gradient(to right, #7C3AED, #2563EB)',
            }}
            title={
              currentUser && !isAuthenticated
                ? 'Please verify your email to access the Forge'
                : isMaintenanceMode && !isAdmin
                ? 'The Forge is currently down for maintenance'
                : ''
            }
          >
            {isLoading ? 'Loading...' : 'Enter the Forge'}
          </Button>

          {!currentUser && (
            <Button
              variant="outline"
              size="lg"
              onClick={openAuthModal}
              className="text-lg px-8 py-6 transition-none"
            >
              Sign In
            </Button>
          )}
        </div>

        <div className="text-center mb-8">
          <p className="text-lg text-muted-foreground">
            {!currentUser
              ? 'Sign in to start playing for free. Unlock premium features if you like it!'
              : !isAuthenticated
              ? 'Please verify your email to access the Forge.'
              : "You're all set! Click 'Enter the Forge' to start playing."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-4xl mx-auto">
          {/* Starting Experience */}
          <div className="bg-card rounded-xl p-6 shadow-lg border border-border relative">
            <h3 className="text-2xl font-bold mb-2 flex items-center text-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Starting Experience
            </h3>

            <p className="text-muted-foreground mb-4">Sign in and start crafting right away</p>

            <div className="mb-4 border-b border-border pb-4">
              <span className="text-3xl font-bold text-foreground">Free</span>
              <span className="text-muted-foreground ml-2">to get started</span>
            </div>

            <ul className="space-y-2 mb-6">
              {[
                'Access to Item & Location Forges',
                '50 free actions to start',
                '5 skips to use on difficult goals',
                'Stat tracking',
                'Saved progression',
              ].map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="h-5 w-5 text-primary mr-2 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Premium Features */}
          <div className="bg-card rounded-xl p-6 shadow-lg border border-border relative">
            <h3 className="text-2xl font-bold mb-2 flex items-center text-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
              Premium Features
            </h3>

            <p className="text-muted-foreground mb-4">Unlocked with your first purchase</p>

            <div className="mb-4 border-b border-border pb-4">
              <span className="text-3xl font-bold text-foreground">$1</span>
              <span className="text-muted-foreground ml-2">for 200 actions</span>
            </div>

            <ul className="space-y-2 mb-6">
              {[
                'All starting features',
                'Access to Character Forge',
                'Chance for Deconstructor keys',
                'Priority support',
              ].map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="h-5 w-5 text-primary mr-2 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Footer with Privacy Policy Link */}
      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p className="mt-2">© {new Date().getFullYear()} VibeCraft. All rights reserved.</p>
      </div>

      {/* Persistent Privacy Policy Link - Bottom Right */}
      <div className="fixed bottom-4 right-4 z-10">
        <Button
          variant="link"
          size="sm"
          className="text-muted-foreground hover:text-foreground text-xs opacity-70 hover:opacity-100"
          onClick={() => navigate('/privacy', { state: { fromLanding: true } })}
        >
          Privacy Policy
        </Button>
      </div>

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
};

export default LandingPage;
